import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, filter, of, switchMap, take } from 'rxjs';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { SharedModule } from '@shared/shared.module';
import { AppState } from '@src/app/state';
import { selectCompanyId } from '@src/app/state/auth/auth.selectors';
import { selectPageFilterCompanyIds } from '@src/app/state/global/global.selectors';
import { Option } from '@interfaces/common/option';
import {
  AccountingDocumentListItem,
  AccountingDocumentStatus,
} from '@interfaces/accounting-data-standardization/responses/accounting-document-list-item.response';
import {
  parseStatusesQueryParam,
  parseYearsQueryParam,
  URL_PARAM_STATUSES,
  URL_PARAM_YEARS,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/status-query-param.util';
import { SelectCompanyComponent } from '@shared/components/select-company/select-company.component';
import { parseAnyCompanyQueryParam } from '@shared/components/select-company/company-query-param.util';
import {
  AccountingDataStandardizationStateService,
  URL_PARAM_DOCUMENT,
  URL_PARAM_DOCUMENT_COMPANY,
  URL_PARAM_STATEMENT,
  URL_PARAM_TAB,
  parseUrlStatement,
  parseUrlTab,
} from '@src/app/features/dashboard/accounting-data-standardization/services/accounting-data-standardization-state.service';
import { YearsWithoutDocumentBannerComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/years-without-document-banner/years-without-document-banner.component';
import { DocumentListComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/document-list/document-list.component';
import { AccountingDocumentDetailComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/document-detail/document-detail.component';
import {
  DeParaOverwriteGuardService,
  DeParaOverwriteTarget,
} from '@services/helpers/de-para-overwrite/de-para-overwrite-guard.service';
import { FinancialStatementType } from '@interfaces/accounting-data-standardization/responses/statement-detail.response';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import {
  ExceptionQueueOpenRequest,
  ExceptionQueuePanelComponent,
} from '@src/app/features/dashboard/accounting-data-standardization/components/exception-queue-panel/exception-queue-panel.component';
import { StandardAccountPromotionsPanelComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/standard-account-promotions-panel/standard-account-promotions-panel.component';
import { StandardAccountDictionaryGovernanceService } from '@app/core/services/http/accounting-data-standardization/standard-account-dictionary-governance.service';
import { StandardAccountDictionaryProposalSummary } from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary-governance.response';
import { StandardAccountDictionaryPanelComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/standard-account-dictionary-panel/standard-account-dictionary-panel.component';

import { CompanySelectionService } from '@services/helpers/company-selection.service';
/**
 * Aba da listagem (PBI 3423): os documentos (o de sempre), os retidos na fila de exceção e, só ADMIN, as propostas de
 * conta oficial e o dicionário do plano padrão. Não é rota nova nem vai para a URL: é só a visão da listagem.
 */
type ListTab = 'documents' | 'exceptions' | 'promotions' | 'dictionary';

@Component({
  selector: 'app-accounting-data-standardization',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    SelectCompanyComponent,
    DocumentListComponent,
    AccountingDocumentDetailComponent,
    ExceptionQueuePanelComponent,
    StandardAccountPromotionsPanelComponent,
    StandardAccountDictionaryPanelComponent,
    YearsWithoutDocumentBannerComponent,
  ],
  providers: [AccountingDataStandardizationStateService],
  templateUrl: './accounting-data-standardization.component.html',
  styleUrls: ['./accounting-data-standardization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountingDataStandardizationComponent extends VaultBaseComponent implements OnInit {
  /** Dono unico da selecao de empresas; monta `companyId` (`all`, CSV curto ou nada acima do limite), card 3571. */
  private readonly companySelection = inject(CompanySelectionService);

  protected readonly stateService = inject(AccountingDataStandardizationStateService);
  protected override readonly store = inject(Store<AppState>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly deParaOverwriteGuard = inject(DeParaOverwriteGuardService);

  private readonly permissions = inject(PermissionsService);
  private readonly dictionaryGovernance = inject(StandardAccountDictionaryGovernanceService);

  /** Aba da listagem. Volta a "Documentos" ao recarregar a tela. */
  protected readonly listTab = signal<ListTab>('documents');
  /** "Retidos" só com a permissão própria da fila de exceção (a mesma que o backend confere). */
  protected readonly canSeeExceptionQueue = toSignal(
    this.permissions.hasPermission(Permissions.AccountingExceptionQueue.LIST),
    { initialValue: false }
  );
  /** "Propostas de conta" só com a permissão de listar propostas (ADMIN). */
  protected readonly canSeePromotions = toSignal(
    this.permissions.hasPermission(Permissions.AccountingStandardAccountPromotion.LIST),
    { initialValue: false }
  );
  /** "Dicionário" só com a permissão de listar o dicionário (ADMIN). */
  protected readonly canSeeDictionary = toSignal(
    this.permissions.hasPermission(Permissions.AccountingStandardAccountDictionary.LIST),
    { initialValue: false }
  );
  /**
   * Contador da aba "Dicionário" (task 3452): mudanças a revisar mais propostas pendentes. Carrega uma vez ao abrir a
   * tela e se atualiza pelo painel quando ele recarrega.
   */
  protected readonly dictionaryAttentionCount = signal(0);
  /** Com uma aba só, a linha de abas some e a tela fica como era. */
  protected readonly hasListTabs = computed(
    () => this.canSeeExceptionQueue() || this.canSeePromotions() || this.canSeeDictionary()
  );

  protected setListTab(tab: ListTab): void {
    this.listTab.set(tab);
  }

  protected onDictionarySummaryChanged(summary: StandardAccountDictionaryProposalSummary): void {
    this.dictionaryAttentionCount.set((summary.toReview ?? 0) + (summary.pending ?? 0));
  }

  /** Busca a contagem do dicionário só para quem pode ver a aba; falha deixa a aba sem contador. */
  private loadDictionaryAttention(): void {
    this.permissions
      .hasPermission(Permissions.AccountingStandardAccountDictionary.LIST)
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() =>
          this.dictionaryGovernance.getProposalSummary().pipe(catchError(() => of(null)))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(summary => summary && this.onDictionarySummaryChanged(summary));
  }

  /**
   * Abre um documento retido. A fila traz só empresa e id: o caminho é o mesmo do link compartilhável (`doc` +
   * `docCompanyId` na URL), que o `watchDocumentUrlState` transforma no detalhe. Sem `replaceUrl`, para o Voltar do
   * navegador trazer de volta a fila.
   */
  protected onOpenQueueDocument(request: ExceptionQueueOpenRequest): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        [URL_PARAM_DOCUMENT]: request.extractionId,
        [URL_PARAM_DOCUMENT_COMPANY]: request.companyId,
      },
      queryParamsHandling: 'merge',
    });
  }

  protected readonly countLabel = computed<string>(() => {
    const total = this.stateService.total();
    return `${total} ${total === 1 ? 'documento' : 'documentos'}`;
  });

  /**
   * Re-extração seletiva. A extração reenfileirada a pedido roda o De/Para de novo substituindo as linhas
   * ajustadas à mão ou ignoradas (backend, PR #5507): se algum documento de Estrutura Contábil selecionado tem
   * ajuste, pergunta antes. Sem ajuste, segue direto como sempre.
   */
  protected onReprocessSelected(ids: string[]): void {
    const targets: DeParaOverwriteTarget[] = this.stateService
      .documents()
      .filter(doc => ids.includes(doc.id) && doc.type === 'EstruturaContabil')
      .map(doc => ({
        companyId: doc.companyId,
        extractionId: doc.id,
        statementTypes: [
          ...(doc.hasDre ? ['Dre' as FinancialStatementType] : []),
          ...(doc.hasBalanco ? ['Balanco' as FinancialStatementType] : []),
        ],
      }));

    this.deParaOverwriteGuard
      .confirmIfNeeded(targets)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => confirmed && this.stateService.reprocessSelected(ids));
  }

  /** Status do `?statuses=` de entrada, aplicados no `init` do state service. */
  private initialStatuses: AccountingDocumentStatus[] = [];
  /** Anos do `?years=` de entrada (card 3700, "Abrir na Padronização" da Análise Econômica), aplicados depois do `init`. */
  private initialYears: number[] = [];

  ngOnInit(): void {
    this.loadDictionaryAttention();
    // `parseAnyCompanyQueryParam` também aceita os aliases legados (`companyIds`, `COMPANYID`), que
    // `syncCompanyIdsToUrl` normaliza para o parâmetro canônico logo em seguida.
    const urlCompanyIds = parseAnyCompanyQueryParam(this.route.snapshot.queryParams);
    const externalEntry = this.isExternalDocumentEntry();
    // Filtro de status de ENTRADA (`?statuses=`), vale em navegação interna também: a tela nunca grava status na
    // URL, então ele não tem como ser memória dela mesma (a pegadinha do `companyId`). Sai da URL em seguida.
    this.initialStatuses = parseStatusesQueryParam(
      this.route.snapshot.queryParams[URL_PARAM_STATUSES]
    );
    this.initialYears = parseYearsQueryParam(this.route.snapshot.queryParams[URL_PARAM_YEARS]);

    // 1º: `?companyId=id1,id2` da URL, mas SÓ quando é deep link de verdade — ver
    // `isExternalDocumentEntry`. 2º: filtro de empresa herdado de outra tela.
    // `GlobalState.pageFilterCompanyIds` (persistido em localStorage) é onde todo `app-select-company`
    // com `selectionScope="page-filter"` grava sua seleção, então quem filtrou duas empresas em Gestão de
    // Empréstimos chega aqui com as mesmas duas — o filtro é do usuário, não de cada tela.
    // 3º: `selectCompanyId` (escopo do login) só pré-seleciona quando não há filtro nenhum a herdar, para
    // não trocar o contexto do usuário sem pedir. Sem nada disso a tela abre listando todas as empresas do
    // escopo — ela nunca depende de uma empresa escolhida para abrir.
    this.store
      .select(selectPageFilterCompanyIds)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(inheritedIds => {
        const inherited = (inheritedIds ?? []).map(id => String(id)).filter(Boolean);

        if (urlCompanyIds.length > 0 && (externalEntry || inherited.length === 0)) {
          this.initScopeAndWatchUrl(urlCompanyIds);
          return;
        }

        if (inherited.length > 0) {
          this.initScopeAndWatchUrl(inherited);
          return;
        }

        this.store
          .select(selectCompanyId)
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe(id => this.initScopeAndWatchUrl(id ? [id] : []));
      });
  }

  /**
   * Inicializa o escopo de empresa e só ENTÃO começa a observar os query params do documento.
   *
   * A ordem é obrigatória: `init` reseta a tela inteira (volta para a listagem, limpa filtros). Se a
   * observação da URL começasse antes, o `init` — que só roda quando o NgRx emite, de forma assíncrona —
   * chegaria depois e apagaria o documento recém-aberto pelo deep link. Antes desta ordenação o
   * comportamento dependia de a requisição do documento ser mais lenta que o store: funcionava por
   * acidente no deep link e falhava num clique feito logo após a tela abrir.
   *
   * @param companyIds Empresas do escopo resolvido para a listagem.
   */
  private initScopeAndWatchUrl(companyIds: string[]): void {
    this.stateService.init(companyIds, this.initialStatuses);
    // O `init` zera o filtro de ano; o de entrada entra logo depois (a carga do `init` é descartada pela sequência).
    if (this.initialYears.length > 0) {
      this.stateService.setFilterYears(this.initialYears);
      this.initialYears = [];
    }
    this.syncCompanyIdsToUrl(companyIds);
    this.watchDocumentUrlState();
  }

  /**
   * Mantém o estado da tela em sincronia com os query params que descrevem onde o usuário está
   * (`doc`, `docCompanyId`, `tab`, `statement`).
   *
   * É esta assinatura que dá **Voltar/Avançar do navegador** à feature: o histórico do browser emite
   * um `queryParamMap` novo a cada passo, e o state service reconstrói o detalhe (ou volta à lista) a
   * partir dele. Também cobre o deep link — a primeira emissão já traz os params da URL de entrada,
   * então um link colado abre direto no documento e na aba certos.
   *
   * Só reage aos params desta tela; mudanças de `companyId` (o filtro da listagem) seguem tratadas
   * pelo fluxo de empresa no `ngOnInit`.
   */
  private watchDocumentUrlState(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params =>
        this.stateService.applyUrlState(
          params.get(URL_PARAM_DOCUMENT),
          params.get(URL_PARAM_DOCUMENT_COMPANY),
          parseUrlTab(params.get(URL_PARAM_TAB)),
          parseUrlStatement(params.get(URL_PARAM_STATEMENT))
        )
      );
  }

  /**
   * A tela entrou por fora da SPA (link colado/compartilhado, favorito, F5, aba nova) em vez de por uma
   * navegação interna (clique de menu, Voltar do navegador)? É a única situação em que o `companyId` da
   * URL representa a intenção atual de quem abriu a tela.
   *
   * Sem essa distinção o `companyId` da URL vira memória da própria tela e sobrescreve o filtro do
   * usuário — bug real, reproduzido por dois caminhos:
   * - **Voltar do navegador**: `syncCompanyIdsToUrl` grava a seleção com `replaceUrl: true`, então a
   *   entrada de histórico desta rota guarda a última seleção feita AQUI. Quem filtrou outras empresas em
   *   Gestão de Empréstimos e voltou pelo histórico recebia de volta a seleção antiga desta tela.
   * - **Link de menu congelado**: `app-top-menu` monta o `companyId` a partir da URL corrente; enquanto
   *   ele não reagia a mudança só de query param, o link chegava aqui com a empresa de duas telas atrás.
   *
   * Pior que exibir errado: a seleção obedecida é regravada em `pageFilterCompanyIds` pelo
   * `app-select-company`, ou seja, o filtro novo do usuário era destruído para todas as telas.
   *
   * Deep link legítimo continua funcionando nos dois cenários reais: aberto de fora, é a primeira
   * navegação do documento (`previousNavigation == null`); aberto por quem não estava logado, passa pelo
   * login, que limpa `pageFilterCompanyIds` (`clearLocalCompanyScopeArtifacts`) e cai na regra "sem
   * filtro global a URL vence".
   */
  private isExternalDocumentEntry(): boolean {
    const navigation = this.router.getCurrentNavigation() ?? this.router.lastSuccessfulNavigation();
    return navigation ? navigation.previousNavigation == null : true;
  }

  /** Aplica a seleção do multiselect de empresas ao filtro da lista e reflete na URL. */
  protected onCompaniesChange(options: Option[] | null): void {
    const ids = (options ?? []).map(option => String(option.value)).filter(Boolean);
    this.stateService.setCompanyIds(ids);
    this.syncCompanyIdsToUrl(ids);
  }

  /**
   * Reflete o filtro de empresa na URL (`?companyId=id1,id2`), no mesmo padrão das outras telas com filtro
   * local de empresa (`buildLocalCompanyQueryParam`, que ainda limpa os aliases legados). `merge` preserva
   * os outros query params da rota e `replaceUrl` evita empilhar uma entrada de histórico por troca de
   * filtro — o botão Voltar do navegador continua saindo da tela, não desfazendo filtro por filtro.
   * Seleção vazia grava `companyId: null`, ou seja, remove o parâmetro (= todas as empresas do escopo).
   */
  private syncCompanyIdsToUrl(companyIds: string[]): void {
    this.router.navigate([], {
      relativeTo: this.route,
      // `statuses` é só de entrada (ver `ngOnInit`): `null` o tira da URL na primeira sincronização, para um F5
      // depois de o usuário mexer no filtro não reaplicar o recorte do link.
      queryParams: {
        ...this.companySelection.buildQueryParam(companyIds),
        [URL_PARAM_STATUSES]: null,
        [URL_PARAM_YEARS]: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Abrir um documento da lista. Sem guarda de edição pendente de propósito: a lista só aparece fora do
   * detalhe, e sair do detalhe já passa pela guarda (`AccountingDocumentDetailComponent.onBack`) e zera as
   * pendências (`closeDocument`) — não existe edição pendente a proteger aqui.
   */
  protected onOpenDocument(item: AccountingDocumentListItem): void {
    this.stateService.openDocument(item);
  }
}
